import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, XCircle, ExternalLink, RefreshCw, Mail, CheckCircle2 } from "lucide-react";
import { waLink } from "@/lib/phoneUtils";

const cfg = { withCredentials: true };

const daysSince = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
};

export default function GuardianIntentsPanel({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [showOld, setShowOld] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/guardians?status=pending_fonzip,pending_payment,payment_reported`, cfg);
      setRows(r.data.guardians || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bekleyen kayıtlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    if (!window.confirm("Bu tamamlanmamış kayıt iptal edilsin mi?")) return;
    setBusy(id);
    try {
      await axios.post(`${API}/admin/guardians/${id}/cancel`, {}, cfg);
      toast.success("Kayıt iptal edildi");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İptal edilemedi");
    } finally {
      setBusy("");
    }
  };

  const activate = async (id, name) => {
    if (!window.confirm(`${name || "Bu kayıt"} için ödeme hesaba geçti mi? Onaylarsan aktif Koruyucu Aile olur ve karşılama e-postası gider.`)) return;
    setBusy(id);
    try {
      await axios.post(`${API}/admin/guardians/${id}/activate`, {}, cfg);
      toast.success("Koruyucu Aile aktifleştirildi");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktifleştirilemedi");
    } finally {
      setBusy("");
    }
  };

  const remind = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/admin/guardians/${id}/send-reminder`, {}, cfg);
      toast.success(r.data.sent ? `Hatırlatma gönderildi: ${r.data.email}` : "E-posta gönderilemedi");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hatırlatma gönderilemedi");
    } finally {
      setBusy("");
    }
  };

  const label = (s) => s === "pending_fonzip" ? "Fonzip ödemesi bekleniyor"
    : s === "payment_reported" ? "Havale bildirildi — kontrol bekliyor"
    : "Ödeme bekleniyor";

  const fresh = rows.filter((g) => daysSince(g.created_at) <= 30);
  const oldCount = rows.length - fresh.length;
  const visible = showOld ? rows : fresh;

  return (
    <div className="space-y-4" data-testid="guardian-intents-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-xl">Bekleyen Kayıtlar</h2>
          <p className="text-sm text-muted-foreground">
            Form doldurulmuş ama ödeme tamamlanmamış başvurular. Ödeme Fonzip'te onaylanınca otomatik olarak Koruyucu Aile olur.
            <br className="hidden sm:block" />
            Otomatik hatırlatma: <strong>2. gün</strong> ve <strong>7. gün</strong> (en fazla 2 e-posta), sonra rahat bırakılır.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            disabled={busy === "sweep"}
            onClick={async () => {
              setBusy("sweep");
              try {
                const dry = await axios.post(`${API}/admin/guardians/pending-reminders/run?dry_run=true`, {}, cfg);
                const n = (dry.data.planned || []).length;
                if (!n) { toast.info("Şu an hatırlatma gönderilecek kayıt yok (2. ve 7. günde gönderilir)"); return; }
                if (!window.confirm(`${n} kişiye hatırlatma e-postası gönderilecek. Devam edilsin mi?`)) return;
                const r = await axios.post(`${API}/admin/guardians/pending-reminders/run`, {}, cfg);
                toast.success(`${r.data.sent} hatırlatma gönderildi`);
                await load();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Tarama başarısız");
              } finally { setBusy(""); }
            }}
            data-testid="intents-run-reminders"
          >
            <Mail className="w-4 h-4 mr-1" /> Hatırlatmaları tara
          </Button>
          {oldCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowOld((v) => !v)} data-testid="intents-toggle-old">
              {showOld ? "Sadece son 30 günü göster" : `30 günden eskileri de göster (${oldCount})`}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="intents-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Yenile
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="spinner" /></div>
      ) : visible.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <Clock className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground" data-testid="intents-empty">
            {oldCount > 0 ? "Son 30 günde bekleyen kayıt yok." : "Bekleyen kayıt yok."}
          </p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {visible.map((g) => (
            <Card key={g.guardian_id} data-testid={`intent-row-${g.guardian_id}`}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">
                    {g.guardian_name} <span className="text-muted-foreground font-normal">→ {g.dog_name || "—"}</span>
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {g.guardian_email || "e-posta yok"} · {g.guardian_phone || "telefon yok"} · {Number(g.monthly_amount || 0).toLocaleString("tr-TR")} TL/ay
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="secondary">{label(g.status)}</Badge>
                    <span className="text-xs text-muted-foreground">{daysSince(g.created_at)} gün önce</span>
                    {g.fonzip_ref_code && <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{g.fonzip_ref_code}</code>}
                    {g.fonzip_reminder_count > 0 && (
                      <Badge variant="outline" className="text-xs">
                        <Mail className="w-3 h-3 mr-1" /> {g.fonzip_reminder_count} hatırlatma gönderildi
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    className="bg-primary text-primary-foreground"
                    disabled={busy === g.guardian_id}
                    onClick={() => activate(g.guardian_id, g.guardian_name)}
                    data-testid={`intent-activate-${g.guardian_id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Ödemeyi Onayla
                  </Button>
                  {g.status === "pending_fonzip" && g.guardian_email && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy === g.guardian_id}
                      onClick={() => remind(g.guardian_id)}
                      data-testid={`intent-remind-${g.guardian_id}`}
                    >
                      <Mail className="w-3.5 h-3.5 mr-1" /> Hatırlatma gönder
                    </Button>
                  )}
                  {g.guardian_phone && (
                    <a
                      href={waLink(g.guardian_phone, `Merhaba ${g.guardian_name}, ${g.dog_name || "can dostumuz"} için Koruyucu Aile başvurunu aldık. Aylık desteğini tamamlamak için Fonzip sayfasından ödemeyi bitirmen yeterli. Yardıma ihtiyacın olursa buradayız 💛`)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`intent-whatsapp-${g.guardian_id}`}
                    >
                      <Button size="sm" variant="outline" className="text-green-700 border-green-200 hover:bg-green-50">
                        WhatsApp ile hatırlat <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </a>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    disabled={busy === g.guardian_id}
                    onClick={() => cancel(g.guardian_id)}
                    data-testid={`intent-cancel-${g.guardian_id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> İptal et
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
